import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { UsageData, SubscriptionPlan, BillingHistoryRow } from '../types/subscription';

export interface PlanTierGroup {
  base_plan: string;
  plans: Record<number, SubscriptionPlan>;
}

interface UseSubscriptionReturn {
  usageData: UsageData | null;
  allPlans: SubscriptionPlan[];
  plansByTier: PlanTierGroup[];
  availableTerms: number[];
  billingHistory: BillingHistoryRow[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useSubscription(): UseSubscriptionReturn {
  const { user } = useAuth();
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [allPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingHistoryRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsageData = useCallback(async () => {
    if (!user?.client_id) return;

    try {
      const { data, error: rpcError } = await supabase.rpc('get_current_usage', {
        p_client_id: user.client_id,
      });

      if (rpcError) throw rpcError;
      if (data) {
        setUsageData(data as UsageData);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch usage data';
      setError(message);
    }
  }, [user?.client_id]);

  const fetchAllPlans = useCallback(async () => {
    try {
      const { data, error: plansError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('prepaid_months', { ascending: true });

      if (plansError) throw plansError;
      if (data) {
        setAllPlans(data as SubscriptionPlan[]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch plans';
      setError(message);
    }
  }, []);

  const fetchBillingHistory = useCallback(async () => {
    if (!user?.client_id) return;

    try {
      const { data, error: historyError } = await supabase
        .from('billing_usage_monthly')
        .select('*')
        .eq('client_id', user.client_id)
        .order('period_start', { ascending: false })
        .limit(12);

      if (historyError) throw historyError;
      if (data) {
        setBillingHistory(data as BillingHistoryRow[]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch billing history';
      setError(message);
    }
  }, [user?.client_id]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([fetchUsageData(), fetchAllPlans(), fetchBillingHistory()]);
    setIsLoading(false);
  }, [fetchUsageData, fetchAllPlans, fetchBillingHistory]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const plansByTier = useMemo(() => {
    const tierMap = new Map<string, Record<number, SubscriptionPlan>>();
    const tierOrder: string[] = [];

    for (const plan of allPlans) {
      if (!tierMap.has(plan.base_plan)) {
        tierMap.set(plan.base_plan, {});
        tierOrder.push(plan.base_plan);
      }
      tierMap.get(plan.base_plan)![plan.prepaid_months] = plan;
    }

    return tierOrder.map((base_plan) => ({
      base_plan,
      plans: tierMap.get(base_plan)!,
    }));
  }, [allPlans]);

  const availableTerms = useMemo(() => {
    const terms = new Set<number>();
    for (const plan of allPlans) {
      terms.add(plan.prepaid_months);
    }
    return Array.from(terms).sort((a, b) => a - b);
  }, [allPlans]);

  return {
    usageData,
    allPlans,
    plansByTier,
    availableTerms,
    billingHistory,
    isLoading,
    error,
    refresh,
  };
}
